// components/session/SessionAlerts.js - SYSTÈME D'ALERTES SESSION
import React, { useState, useEffect, useCallback } from 'react';
import { Toast } from '../ui/Toast';
import SessionConfirmDialog from '../SessionConfirmDialog';
import { Clock, AlertTriangle, Zap, RefreshCw } from 'lucide-react';

/**
 * 🔥 SESSION ALERTS - SYSTÈME D'ALERTES INTELLIGENT
 * - Toast pour alertes 3min/1min (WARNING/URGENT)
 * - Modal critique pour 30sec (CRITICAL)
 * - Modal suggestion pour inactivité (SUGGESTION)
 * - Son d'alerte pour urgence
 * - Suggestions intelligentes de prolongation
 */
const SessionAlerts = ({ 
  alertLevel, 
  timeLeft, 
  sessionInfo, 
  onExtend, 
  onLogout, 
  isActive = true 
}) => {
  // États locaux
  const [showToast, setShowToast] = useState(false);
  const [showCriticalModal, setShowCriticalModal] = useState(false);
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [isExtending, setIsExtending] = useState(false);
  const [hasPlayedSound, setHasPlayedSound] = useState(false);
  const [lastAlertLevel, setLastAlertLevel] = useState(null);

  /**
   * 🔥 GÉNÉRATION SON D'ALERTE (JS)
   */
  const playAlertSound = useCallback(() => {
    try {
      // Créer contexte audio
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Son de bip d'urgence (double bip)
      const beep = (frequency, duration) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
        
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + duration);
      };

      // Double bip d'urgence
      beep(800, 0.2); // Premier bip aigu
      setTimeout(() => beep(600, 0.3), 300); // Deuxième bip plus grave
      
    } catch (error) {
      console.warn('⚠️ [SessionAlerts] Impossible de jouer le son:', error);
    }
  }, []);

  /**
   * 🔥 FORMATER TEMPS RESTANT
   */
  const formatTimeLeft = useCallback((ms) => {
    const minutes = Math.floor(ms / (60 * 1000));
    const seconds = Math.floor((ms % (60 * 1000)) / 1000);
    
    if (minutes > 0) {
      return `${minutes}min ${seconds}s`;
    }
    return `${seconds}s`;
  }, []);

  /**
   * 🔥 GESTION DES ALERTES SELON LE NIVEAU
   */
  useEffect(() => {
    if (!isActive || !alertLevel || alertLevel === lastAlertLevel) return;

    const timeLeftMinutes = Math.floor((timeLeft || 0) / (60 * 1000));
    const timeLeftFormatted = formatTimeLeft(timeLeft || 0);

    switch (alertLevel) {
      case 'WARNING': // 3min restantes (à 12min)
        console.log(`📢 [SessionAlerts] Affichage alerte ${alertLevel} — Temps restant: ${timeLeftFormatted}`);
        setToastMessage(`⏰ Votre session expire dans ${timeLeftMinutes} minutes`);
        setShowToast(true);
        break;

      case 'URGENT': // 1min restante (à 14min)
        console.log(`📢 [SessionAlerts] Affichage alerte ${alertLevel} — Temps restant: ${timeLeftFormatted}`);
        setToastMessage(`⚠️ Session expire dans ${timeLeftMinutes} minute - Prolonger ?`);
        setShowToast(true);
        break;

      case 'CRITICAL': // 30sec restantes (à 14min30)
        console.log(`📢 [SessionAlerts] Affichage alerte ${alertLevel} — Temps restant: ${timeLeftFormatted}`);
        setShowCriticalModal(true);
        if (!hasPlayedSound) {
          playAlertSound();
          setHasPlayedSound(true);
        }
        break;

      case 'SUGGESTION': // Inactivité détectée
        console.log(`📢 [SessionAlerts] Affichage alerte ${alertLevel} — Temps restant: ${timeLeftFormatted}`);
        setShowSuggestionModal(true);
        break;

      default:
        break;
    }

    setLastAlertLevel(alertLevel);
  }, [alertLevel, timeLeft, isActive, lastAlertLevel, hasPlayedSound, playAlertSound, formatTimeLeft]);

  /**
   * 🔥 RESET SOUND FLAG QUAND ALERT LEVEL CHANGE
   */
  useEffect(() => {
    if (alertLevel !== 'CRITICAL') {
      setHasPlayedSound(false);
    }
  }, [alertLevel]);

  /**
   * 🔥 PROLONGER SESSION
   */
  const handleExtendSession = useCallback(async (reason = 'user_request') => {
    try {
      setIsExtending(true);
      const result = await onExtend(reason);
      
      if (result.success) {
        // Fermer tous les modals
        setShowCriticalModal(false);
        setShowSuggestionModal(false);
        setShowToast(false);
        
        // Toast de confirmation avec ton style
        setToastMessage('✅ Session prolongée avec succès');
        setShowToast(true);
        
        // Reset des états
        setLastAlertLevel(null);
        setHasPlayedSound(false);
      } else {
        throw new Error(result.error || 'Erreur prolongation');
      }
    } catch (error) {
      console.error('❌ [SessionAlerts] Erreur prolongation:', error);
      setToastMessage(`❌ Erreur: ${error.message}`);
      setShowToast(true);
    } finally {
      setIsExtending(false);
    }
  }, [onExtend]);

  /**
   * 🔥 LOGOUT FORCÉ
   */
  const handleForceLogout = useCallback(async () => {
    try {
      await onLogout('user_choice');
    } catch (error) {
      console.error('❌ [SessionAlerts] Erreur logout:', error);
    }
  }, [onLogout]);

  /**
   * 🔥 FERMER TOAST
   */
  const handleCloseToast = useCallback(() => {
    setShowToast(false);
  }, []);

  // Ne pas rendre si pas actif
  if (!isActive) return null;

  return (
    <>
      {/* 🔔 TOAST ALERTS (WARNING/URGENT) - TON STYLE EXISTANT */}
      {showToast && (
        <Toast
          message={toastMessage}
          type={toastMessage.includes('❌') ? 'error' : 'success'}
          onClose={handleCloseToast}
        />
      )}

      {/* ⚠️ MODAL CRITIQUE (30sec) - TON CONFIRMDIALOG STYLE */}
      <SessionConfirmDialog
        isOpen={showCriticalModal}
        title={timeLeft > 60000 ? 
          `⚠️ Session expire dans ${Math.ceil(timeLeft / 60000)} minute !` : 
          `⚠️ Session expire dans ${Math.ceil(timeLeft / 1000)} secondes !`}
        description={`Votre session va expirer dans ${formatTimeLeft(timeLeft || 0)}. Voulez-vous la prolonger maintenant ou vous déconnecter ?`}
        confirmText={isExtending ? "Prolongation..." : "🔄 Prolonger (+10min)"}
        cancelText="🚪 Me déconnecter"
        type="warning"
        customIcon={<AlertTriangle size={32} />}
        onConfirm={() => handleExtendSession('critical_extension')}
        onClose={handleForceLogout}
      />

      {/* 💡 MODAL SUGGESTION (Inactivité) - TON STYLE */}
      <SessionConfirmDialog
        isOpen={showSuggestionModal}
        title="💡 Prolonger votre session ?"
        description={`Vous êtes inactif depuis 2 minutes. Votre session expire dans ${Math.floor((timeLeft || 0) / (60 * 1000))} minutes. Voulez-vous la prolonger ?`}
        confirmText={isExtending ? "Prolongation..." : "✨ Oui, prolonger"}
        cancelText="⏰ Me rappeler dans 10min"
        type="question"
        customIcon={<Zap size={32} />}
        onConfirm={() => handleExtendSession('activity_suggestion')}
        onClose={() => {
          setShowSuggestionModal(false);
          // Reporter l'alerte de 10 minutes
          setLastAlertLevel(null);
        }}
      />

      {/* 🔧 DEBUG INFO (Development seulement) */}
      {process.env.NODE_ENV === 'development' && alertLevel && (
        <div className="fixed bottom-20 right-4 bg-black bg-opacity-75 text-white p-3 rounded-lg text-xs font-mono z-40">
          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <Clock size={12} />
              <span>Alert: {alertLevel}</span>
            </div>
            <div>Temps: {formatTimeLeft(timeLeft || 0)}</div>
            <div>User: {sessionInfo?.user?.identifiant || 'Unknown'}</div>
            <div className="flex items-center space-x-1">
              <div className={`w-2 h-2 rounded-full ${
                alertLevel === 'CRITICAL' ? 'bg-red-500 animate-pulse' :
                alertLevel === 'URGENT' ? 'bg-yellow-500' :
                alertLevel === 'WARNING' ? 'bg-blue-500' :
                alertLevel === 'SUGGESTION' ? 'bg-purple-500' :
                'bg-green-500'
              }`}></div>
              <span className="text-xs">
                {alertLevel === 'CRITICAL' ? 'CRITIQUE' :
                 alertLevel === 'URGENT' ? 'URGENT' :
                 alertLevel === 'WARNING' ? 'ATTENTION' :
                 alertLevel === 'SUGGESTION' ? 'SUGGESTION' :
                 'NORMAL'}
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SessionAlerts;